<template>
  <div>
    <section
      v-if="$auth.isAuthenticated"
      class="w-full"
    >
      <h1 class="mb-6 text-2xl font-bold">
        Kundenstatus
      </h1>

      <div class="flex flex-wrap gap-4">
        <CustomerStatusBox
          title="Kontakt Akquise"
          :rows="[
            { label: 'Reaktivierung bestehender Kontakte', value: totalInvitationsCount?.toLocaleString() || 0 },
            { label: 'Gewinnung neuer Kontakte', value: `${savedContentsTotal?.toLocaleString() || 0} / ${savedContentsNumberOfPosts?.toLocaleString() || 0}` }
          ]"
          :footer="{ label: 'Zeit- und Kostenersparnis:', value: formatMinutesToHours(timeSaved || 0) }"
          @open-details-dialog="() => showContactAcquisitionModal()"
        />

        <CustomerStatusBox
          title="Automatische E-Mail Funnels"
          :rows="[
            { label: 'gesendet:', value: sent?.toLocaleString() || 0 },
            { label: 'geöffnet:', value: uniqueOpens?.toLocaleString() || 0 },
            { label: 'Klicks:', value: uniqueLinksClicked?.toLocaleString() || 0 },
            { label: 'Zeit- und Kostenersparnis:', value: `${formatMinutesToHours(timeSaved || 0)} | ${formatPrice(moneySaved || 0, isSmgPlatform ? 'CHF' : null)}` }
          ]"
          @open-details-dialog="() => showEmailFunnelsModal()"
        />

        <CustomerStatusBox
          title="Status Ihrer Landingpages/Widgets"
          :rows="[
            { label: 'Aufrufe', value: analyticsImpression?.overallTotal?.toLocaleString() || 0 },
            { label: '# Landingpages/Widgets', value: numberOfLandingpages?.toLocaleString() || 0 }
          ]"
          :footer="{ label: 'Zeit- und Kostenersparnis:', value: `${formatMinutesToHours(timeSaved || 0)} | ${formatPrice(moneySaved || 0, isSmgPlatform ? 'CHF' : null)}` }"
          @open-details-dialog="() => showImpressionsModal()"
        />

        <CustomerStatusBox
          title="Insgesamt gewonnene Kontakte"
          :rows="[
            { label: 'Alle', value: totalLeadCount?.toLocaleString() || 0 },
            { label: 'Mit Bewertung', value: totalLeadCountWithValuation?.toLocaleString() || 0 },
            { label: 'Letzte 30 Tage', value: totalLeadCountLastThirtyDays?.toLocaleString() || 0 }
          ]"
          @open-details-dialog="() => showStatusesStatisticsModal()"
        />

        <CustomerStatusBox
          title="Das haben Sie mit BOTTIMMO insgesamt erreicht"
          no-details
          :rows="[
            { label: 'Immobilienvolumen', value: totalLeadValue || 0 },
            { label: 'Potenzielle Provision - ', value: `${commission}% - ${totalCommissionValue || 0}` }
          ]"
          :footer="{ label: 'Gesamte Zeit- und Kostenersparnis:', value: `${formatMinutesToHours(timeSaved || 0)} | ${formatPrice(moneySaved || 0, isSmgPlatform ? 'CHF' : null)}` }"
        />
      </div>
    </section>
  </div>
</template>
<script>
import STATISTICS from '@/graphql/Statistics.gql'
import GET_SAVED_CONTENTS from '@/graphql/GetSavedContents.gql'
import INVITATION_STATISTICS_FOR_COMPANY from '@/graphql/InvitationStatisticsForCompany.gql'
import EMAIL_FUNNELS_STATISTICS from '@/graphql/EmailFunnelsStatistics.gql'
import GET_NUMBER_OF_LANDINGPAGES from '@/graphql/GetNumberOfLandingpages.gql'
import GET_ANALYTICS_IMPRESSIONS from '@/graphql/GetAnalyticsImpressionForCompany.gql'
import CustomerStatusBox from './customers-status-box.vue'
import ContactAcquisitionModal from './contact-acquisition-details.vue'
import ImpressionsModal from './impressions-details.vue'
import EmailFunnelsModal from './email-funnels-details.vue'
import StatusesStatisticsModal from './statuses-statistics-details.vue'

const MINUTES_PER_POST = 45
const MONEY_PER_MINUTE = 2.5
const MINUTES_PER_MAIL = 25

export default {
  components: {
    CustomerStatusBox
  },
  props: {
    companyId: {
      type: String,
      required: true
    },
    platform: {
      type: String,
      required: false
    }
  },
  data: () => ({
    showAcqusisitionDetails: false,
    totalLeadCount: 0,
    totalLeadCountLastThirtyDays: 0,
    totalLeadCountWithValuation: 0,
    totalLeadValue: 0,
    totalCommissionValue: 0,
    commission: 0,
    savedContentsTotal: 0,
    savedContentsNumberOfPosts: 0,
    totalInvitationsCount: 0,
    invitationsPerLandingpage: [],
    timeSaved: 0,
    moneySaved: 0,
    sent: 0,
    uniqueOpens: 0,
    uniqueLinksClicked: 0,
    numberOfLandingpages: 0,
    analyticsImpression: 0
  }),
  computed: {
    isSmgPlatform () {
      return this.platform?.toLowerCase() === 'smg'
    },
    contentCreatorConfig () {
      return this.$features.feature(this.featureNames?.CONTENT_CREATOR)?.config?.dashboard
    },
    acquisitionBoosterConfig () {
      return this.$features.feature(this.featureNames?.ACQUISITION_BOOSTER)?.config?.dashboard
    }
  },

  async created () {
    const apolloClient = this.$apollo.provider.defaultClient

    try {
      let invitationsTimeSaved = 0
      let invitationsMoneySaved = 0
      let savedContentsTimeSaved = 0
      let savedContentsMoneySaved = 0
      // 1. Fetch contents
      const { data: contentsData } = await apolloClient.query({
        query: GET_SAVED_CONTENTS,
        variables: {
          input: {
            companyId: this.companyId,
            perPage: 1,
            page: 1
          }
        },
        fetchPolicy: 'network-only'
      })

      if (contentsData?.savedContents) {
        const factors = {
          minutesPerPost: this.contentCreatorConfig?.minutesPerPost || MINUTES_PER_POST,
          moneyPerMinute: this.contentCreatorConfig?.moneyPerMinute || MONEY_PER_MINUTE
        }

        this.savedContentsTotal = contentsData?.savedContents.total
        this.savedContentsNumberOfPosts = contentsData?.savedContents.numberOfPosts
        savedContentsTimeSaved = this.savedContentsTotal * factors.minutesPerPost
        savedContentsMoneySaved = savedContentsTimeSaved * factors.moneyPerMinute
      }

      // 2. Fetch invitation stats
      const { data: invitationData } = await apolloClient.query({
        query: INVITATION_STATISTICS_FOR_COMPANY,
        variables: {
          input: { companyId: this.companyId }
        },
        fetchPolicy: 'network-only'
      })

      if (invitationData?.invitationStatisticsForCompany) {
        const { minutesPerMail = MINUTES_PER_MAIL, moneyPerMinute = MONEY_PER_MINUTE } = this.acquisitionBoosterConfig || {}

        this.invitationsPerLandingpage = invitationData?.invitationStatisticsForCompany?.invitationsPerLandingpage
        this.totalInvitationsCount = invitationData.invitationStatisticsForCompany.total
        invitationsTimeSaved = this.totalInvitationsCount * minutesPerMail
        invitationsMoneySaved = invitationsTimeSaved * moneyPerMinute
      }

      // 3. Fetch statistics
      const { data: statisticsData } = await apolloClient.query({
        query: STATISTICS,
        variables: {
          input: { companyId: this.companyId }
        },
        fetchPolicy: 'network-only'
      })

      if (statisticsData?.statistics) {
        this.totalLeadCount = statisticsData.statistics.totalLeadCount.total
        this.totalLeadCountLastThirtyDays = statisticsData.statistics.totalLeadCount.lastThirtyDays
        this.totalLeadCountWithValuation = statisticsData.statistics.totalLeadCount.withValuation
        this.totalLeadValue = this.formatPrice(statisticsData.statistics.totalLeadValue, this.isSmgPlatform ? 'CHF' : null)
        this.totalCommissionValue = this.formatPrice(statisticsData.statistics.totalCommissionValue.value, this.isSmgPlatform ? 'CHF' : null)
        this.commission = this.formatPrice(statisticsData.statistics.totalCommissionValue.commission, this.isSmgPlatform ? 'CHF' : null)
      }

      // Final combined savings
      this.timeSaved = savedContentsTimeSaved + invitationsTimeSaved
      this.moneySaved = savedContentsMoneySaved + invitationsMoneySaved

      const { data: landingpages } = await apolloClient.query({
        query: GET_NUMBER_OF_LANDINGPAGES,
        variables: {
          input: { companyId: this.companyId }
        },
        fetchPolicy: 'network-only'
      })

      if (landingpages) {
        this.numberOfLandingpages = landingpages.numberOfLandingpages
      }

      const { data: analytics } = await apolloClient.query({
        query: GET_ANALYTICS_IMPRESSIONS,
        variables: {
          input: { companyId: this.companyId }
        },
        fetchPolicy: 'network-only'
      })

      if (analytics) {
        this.analyticsImpression = analytics.analyticsImpression
      }

      const { data: emailFunnels } = await apolloClient.query({
        query: EMAIL_FUNNELS_STATISTICS,
        variables: {
          input: { companyId: this.companyId }
        },
        fetchPolicy: 'network-only'
      })

      if (emailFunnels?.companyEmailFunnelsStatistics) {
        this.sent = emailFunnels?.companyEmailFunnelsStatistics?.sent
        this.uniqueOpens = emailFunnels?.companyEmailFunnelsStatistics?.uniqueOpens
        this.uniqueLinksClicked = emailFunnels?.companyEmailFunnelsStatistics?.uniqueLinksClicked
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
    }
  },
  methods: {
    showContactAcquisitionModal () {
      this.$modal.show(
        ContactAcquisitionModal,
        {
          invitationsPerLandingpage: this.invitationsPerLandingpage?.length > 0 ? this.invitationsPerLandingpage
            .filter(invitation => invitation.landingpageName)
            .map(
              (invitation) => {
                return {
                  label: invitation.landingpageName,
                  value: invitation.total
                }
              }
            ) : [],
          companyId: this.companyId
        },
        { height: 'auto', width: '70%' },
        {
          closed: () => {
            this.$modal.hide()
          }
        }
      )
    },
    showImpressionsModal () {
      this.$modal.show(
        ImpressionsModal,
        {
          analyticsImpression: this.analyticsImpression ? this.analyticsImpression : {},
          companyId: this.companyId
        },
        { height: 'auto', width: '90%' },
        {
          closed: () => {
            this.$modal.hide()
          }
        }
      )
    },
    showEmailFunnelsModal () {
      this.$modal.show(
        EmailFunnelsModal,
        {
          companyId: this.companyId
        },
        { height: 'auto', width: '70%' },
        {
          closed: () => {
            this.$modal.hide()
          }
        }
      )
    },
    showStatusesStatisticsModal () {
      this.$modal.show(
        StatusesStatisticsModal,
        {
          companyId: this.companyId
        },
        { height: 'auto', width: '70%' },
        {
          closed: () => {
            this.$modal.hide()
          }
        }
      )
    },
    onSubmit () {
      // this.showAcqusisitionDetails = !this.showAcqusisitionDetails
    },
    formatMinutesToHours (minutes) {
      return `${Math.round(minutes / 60).toLocaleString()} h`
    }
  }
}
</script>
